/**
 * My own status, and whether anybody is actually at this keyboard.
 *
 * Two jobs, and they are here together because they are the two halves of the
 * same question - what the firm should be told about whether to interrupt me.
 *
 * WHAT IS *NOT* HERE: the list of states and their labels. Those come from
 * GET /chat/status/options and are rendered from whatever it returns. A menu
 * written out again in React would be a second statement of what a status may
 * be, and the first time the two disagreed somebody would pick an option the
 * server then refused. The server's lib/chat-status.js owns the registry; the
 * screen draws what it is given and colours it by the `dot` it was sent.
 *
 * A TWO-LINE STORE rather than React context, the same shape lib/unread.js
 * uses and for the same reason: context would mean a provider around the
 * navbar and a re-render of everything inside it to move one small object
 * that three widgets read.
 */
import axios from 'axios';
import { contractorAuth } from './contractorAuth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/** A contractor's token when there is one; otherwise the axios interceptor. */
const authFor = () => contractorAuth() || {};

/* --------------------------------------------------------- am I actually here */

/**
 * Away is the status nobody sets and everybody expects, and the server cannot
 * work it out on its own: the polls run whether or not somebody is at the
 * keyboard, so a laptop left open all night has a perfect heartbeat. The
 * browser is the only thing that knows, so it says - and `active=1` rides on
 * the two polls that already exist rather than on a third request of its own.
 *
 * Hidden tab counts as away immediately. That is deliberate: a tab in the
 * background behind somebody's email is exactly the case Away is for.
 */
const IDLE_MS = 5 * 60 * 1000;
let lastInput = Date.now();

if (typeof window !== 'undefined') {
  const touched = () => { lastInput = Date.now(); };
  for (const type of ['pointerdown', 'keydown', 'touchstart', 'wheel']) {
    window.addEventListener(type, touched, { passive: true });
  }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') touched();
  });
}

/** Is somebody there? What the polls send as `active`. */
export function amActive() {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return false;
  return Date.now() - lastInput < IDLE_MS;
}

/** The query fragment both pollers add, so neither has to remember the name. */
export const activeParam = () => (amActive() ? { active: 1 } : {});

/* ------------------------------------------------------------------- the store */

let mine = null;
const listeners = new Set();

/** What I am currently showing, or null before the first read answers. */
export const myStatus = () => mine;

/** Does my own status mean "make no noise at me"? Read before every chime. */
export const amSilenced = () => Boolean(mine?.silences);

export function setMyStatus(next) {
  mine = next || null;
  for (const fn of listeners) {
    try { fn(mine); } catch { /* one bad listener must not stop the others */ }
  }
  return mine;
}

export function onMyStatus(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Signed out, or switched account: forget it, or the next person inherits it. */
export const forgetMyStatus = () => setMyStatus(null);

/* ----------------------------------------------------------------- the calls */

/** The five states and the clear-after choices, as the server defines them. */
export const options = () => axios
  .get(`${API_BASE}/chat/status/options`, authFor()).then((r) => r.data);

export const load = () => axios
  .get(`${API_BASE}/chat/status/me`, authFor())
  .then((r) => setMyStatus(r.data));

export const save = ({ state, message, emoji, clearAfter }) => axios
  .put(`${API_BASE}/chat/status/me`, {
    state: state || null,
    message: message || null,
    emoji: emoji || null,
    clear_after: clearAfter || null
  }, authFor())
  .then((r) => setMyStatus(r.data));

export const clear = () => axios
  .delete(`${API_BASE}/chat/status/me`, authFor())
  .then((r) => setMyStatus(r.data));
